import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { 
  Settings, Moon, Sun, Volume2, VolumeX, Upload, Download, 
  Trash2, AlertTriangle, Save, X, Info 
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { exportAppData, importAppData } from '../utils/localStorage';
import { playSound } from '../utils/sound';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { 
    darkMode, setDarkMode, soundEnabled, setSoundEnabled, 
    tournament, exportData, importData, resetData
  } = useAppContext();
  
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');
  
  // Handle theme toggle
  const toggleTheme = () => {
    setDarkMode(!darkMode);
    if (soundEnabled) playSound('click', soundEnabled);
  };
  
  // Handle sound toggle
  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
    if (!soundEnabled) {
      // Play sound after enabling it
      setTimeout(() => playSound('click', true), 100);
    }
  };
  
  // Handle export
  const handleExport = () => {
    const data = exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = tournament 
      ? `${tournament.name.replace(/\s+/g, '_')}_export.json` 
      : 'soccer_tournament_export.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    if (soundEnabled) playSound('click', soundEnabled);
  };
  
  // Handle import
  const handleImport = () => {
    try {
      if (!importText.trim()) {
        setImportError('Please provide data to import');
        return;
      }
      
      const success = importData(importText);
      
      if (success) {
        setImportDialogOpen(false);
        setImportText('');
        setImportError('');
        window.location.reload(); // Reload to apply imported data
      } else {
        setImportError('Failed to import data. Please check the format.');
      }
    } catch (error) {
      setImportError('Error importing data: ' + error.message);
    }
  };
  
  // Handle reset data
  const handleReset = () => {
    resetData();
    setResetDialogOpen(false);
    navigate('/');
  };
  
  return (
    <div className="px-4 py-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <Settings size={24} className="mr-2" />
        Settings
      </h1>
      
      <Card className="mb-6">
        <h2 className="text-lg font-semibold mb-4">App Settings</h2>
        
        <div className="space-y-4">
          {/* Dark Mode Toggle */}
          <div className="flex justify-between items-center py-2 border-b dark:border-gray-700 last:border-0">
            <div className="flex items-center">
              {darkMode ? 
                <Moon size={20} className="mr-3 text-blue-500" /> : 
                <Sun size={20} className="mr-3 text-yellow-500" />
              }
              <div>
                <div className="font-medium">Dark Mode</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {darkMode ? 'Currently enabled' : 'Currently disabled'}
                </div>
              </div>
            </div>
            
            <button 
              onClick={toggleTheme}
              className={`w-12 h-6 rounded-full p-1 transition-colors ${
                darkMode ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-700'
              }`}
            >
              <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${
                darkMode ? 'translate-x-6' : ''
              }`} />
            </button>
          </div>
          
          {/* Sound Toggle */}
          <div className="flex justify-between items-center py-2 border-b dark:border-gray-700 last:border-0">
            <div className="flex items-center">
              {soundEnabled ? 
                <Volume2 size={20} className="mr-3 text-green-500" /> : 
                <VolumeX size={20} className="mr-3 text-red-500" />
              }
              <div>
                <div className="font-medium">Sound Effects</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {soundEnabled ? 'Currently enabled' : 'Currently disabled'}
                </div>
              </div>
            </div>
            
            <button 
              onClick={toggleSound}
              className={`w-12 h-6 rounded-full p-1 transition-colors ${
                soundEnabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-700'
              }`}
            >
              <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${
                soundEnabled ? 'translate-x-6' : ''
              }`} />
            </button>
          </div>
        </div>
      </Card>
      
      <Card className="mb-6">
        <h2 className="text-lg font-semibold mb-4">Data Management</h2>
        
        <div className="space-y-3">
          <Button
            secondary
            icon={<Download size={18} />}
            onClick={handleExport}
            className="w-full justify-center"
          >
            Export Tournament Data
          </Button>
          
          <Button
            tertiary
            icon={<Upload size={18} />}
            onClick={() => setImportDialogOpen(true)}
            className="w-full justify-center"
          >
            Import Tournament Data
          </Button>
          
          <Button
            danger
            icon={<Trash2 size={18} />}
            onClick={() => setResetDialogOpen(true)}
            className="w-full justify-center"
          >
            Reset All Data
          </Button>
        </div>
      </Card>
      
      <Card>
        <h2 className="text-lg font-semibold mb-2">About</h2>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          <p className="mb-2">Soccer Tournament &amp; Team Management App</p>
          <p className="mb-2">Version 1.0.0</p>
          <p>A comprehensive app for managing soccer tournaments with real-time updates, detailed analytics, and team management features.</p>
        </div>
      </Card>
      
      {/* Import Dialog */}
      {importDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold flex items-center">
                <Upload size={20} className="mr-2" />
                Import Tournament Data
              </h3>
              <button 
                onClick={() => setImportDialogOpen(false)}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-4">
              <textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Paste exported JSON data here..."
                className="w-full h-48 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 resize-none"
              />
              
              {importError && (
                <div className="mt-2 text-red-500 text-sm flex items-start">
                  <AlertTriangle size={16} className="mr-1 flex-shrink-0 mt-0.5" />
                  <span>{importError}</span>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button
                tertiary
                onClick={() => setImportDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                primary
                icon={<Save size={16} />}
                onClick={handleImport}
              >
                Import Data
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reset Dialog */}
      {resetDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold flex items-center text-red-500">
                <AlertTriangle size={20} className="mr-2" />
                Reset All Data
              </h3>
              <button 
                onClick={() => setResetDialogOpen(false)}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="mb-3">
                Are you sure you want to reset all data? This will delete all tournaments, teams, and matches. This action cannot be undone.
              </p>
              
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 flex items-start">
                <Info size={18} className="text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">
                  You may want to export your data first for backup purposes.
                </span>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button
                tertiary
                onClick={() => setResetDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                danger
                icon={<Trash2 size={16} />}
                onClick={handleReset}
              >
                Reset All Data
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
